import React from "react";
import { useDebouncedCallback } from "use-debounce";
import useInterval from "use-interval";
import { TE_CONSTANTS } from "../../global/constants";
import { ContractsManagerContext, FunctionsContext } from "../../global/contexts";
import { IContract, ITokenContract } from "../../ABIs/contract_map";
import { BigNumber, safeBigNumberDiv, toReadableNumber, toSolidityNumber } from "../../global/utils";
import { ethers } from "ethers";

type SwapActions = "APPROVE" | "SWAP";

type ISwapControlProps = {
  getTokenApprovedAmount: (token: ITokenContract, approvedAddress?: string) => Promise<ethers.BigNumber>;
  getTokenBalance: (token: ITokenContract) => Promise<ethers.BigNumber>;
  swapTokenHolder: (swapData: ISwapData, fromToken: IContract) => Promise<void>;
  swap: (data: ISwapData, token: ITokenContract) => void;
};

const defaultProps: ISwapControlProps = {
  getTokenApprovedAmount: (a, b) => new Promise(() => console.warn("getTokenApprovedAmount not bound")),
  getTokenBalance: (a) => new Promise(() => console.warn("getTokenBalance not bound")),
  swap: (a, b) => new Promise(() => console.warn("swap not bound")),
  swapTokenHolder: (a, b) => new Promise(() => console.warn("swapTokenHolder not bound"))
};

export type ISwapData = {
  price: string;
  guaranteedPrice: string;
  to: string;
  data: string;
  value: string;
  gasPrice: string;
  gas: string;
  estimatedGas: string;
  minimumProtocolFee: string;
  estimatedPriceImpact: string;
  buyAmount: string;
  sellAmount: string;
  sources: ISwapDataSource[];
  buyTokenAddress: string;
  sellTokenAddress: string;
  expectedSlippage: string;
  deadline: string;
};

export type ISwapDataSource = {
  name: string;
  proportion: string;
};

const defaultSwapData: ISwapData = {
  price: "0",
  guaranteedPrice: "0",
  to: "0x00",
  data: "0x00",
  value: "0",
  gasPrice: "0",
  gas: "0",
  estimatedGas: "0",
  minimumProtocolFee: "0",
  estimatedPriceImpact: "0",
  buyAmount: "0",
  sellAmount: "0",
  sources: [],
  buyTokenAddress: "0x00",
  sellTokenAddress: "0x00",
  expectedSlippage: "0",
  deadline: "0"
};

const tokenNames = [
  "TE",
  "USDC",
  "AVAX",
  "BTC.b",
  "WBTC.e",
  "WETH.e"
];

export function SwapControl(props = defaultProps) {
  const { contractManager } = React.useContext(ContractsManagerContext);
  const { approve, subscribeToBlockchain, unsubscribeToBlockchain } = React.useContext(FunctionsContext);

  const [tokenFromBalance, setTokenFromBalance] = React.useState(BigNumber(0));
  const [tokenFrom, setTokenFrom] = React.useState(contractManager.contracts.USDC);

  const [tokenToBalance, setTokenToBalance] = React.useState(BigNumber(0));
  const [tokenTo, setTokenTo] = React.useState(contractManager.contracts.TE);

  const [fromApprovalAmount, setFromApprovalAmount] = React.useState(BigNumber(0));
  const [amountFrom, setAmountFrom] = React.useState(BigNumber(0));
  const [amountFrom_str, setAmountFrom_str] = React.useState("");
  const [amountTo, setAmountTo] = React.useState(BigNumber(0));

  const [currentSlippage, setCurrentSlippage] = React.useState(0.1);
  const [swapData, setSwapData] = React.useState(defaultSwapData);
  const [exchangeRate, setExchangeRate] = React.useState(0);

  const [isLoadingBalance, setIsLoadingBalance] = React.useState(false);
  const [isLoadingApproval, setIsLoadingApproval] = React.useState(false);
  const [isLoadingSwapData, setIsLoadingSwapData] = React.useState(false);
  const [infoExpanded, setInfoExpanded] = React.useState(false);

  function onFromAmountSet(e: any) {
    const val: string = e.target.value.replace(/[^0-9.]/g, "");

    if (val.includes(".") && val.split(".")[1].length > tokenFrom.decimals.toNumber()) {
      return;
    }

    const newFromAmount = toSolidityNumber(val, tokenFrom.decimals);
    setAmountFrom(newFromAmount);
    setAmountFrom_str(val);

    setIsLoadingApproval(true);
    getFromApprovalAmount(tokenFrom);

    setIsLoadingSwapData(true);
    getSwapData();
  };

  function setMaxCash() {
    setAmountFrom(tokenFromBalance);
    setAmountFrom_str(toReadableNumber(tokenFromBalance, tokenFrom.decimals));

    setIsLoadingApproval(true);
    getFromApprovalAmount(tokenFrom);

    setIsLoadingSwapData(true);
    getSwapData();
  };

  function onFromCurrencySet(e: React.ChangeEvent<HTMLSelectElement>) {
    const contracts = contractManager.contracts as unknown as { [key: string]: ITokenContract; };
    const newTokenFrom = contracts[e.target.value];
    setTokenFromBalance(BigNumber(0));
    setTokenFrom(newTokenFrom);
    setAmountFrom(BigNumber(0));
    setAmountFrom_str("0");
    setAmountTo(BigNumber(0));

    setIsLoadingApproval(true);
    getFromApprovalAmount(newTokenFrom);

    setIsLoadingBalance(true);
    getTokenBalance(newTokenFrom, setTokenFromBalance);
  };

  async function onToCurrencySet(e: React.ChangeEvent<HTMLSelectElement>) {
    const contracts = contractManager.contracts as unknown as { [key: string]: ITokenContract; };
    const newTokenTo = contracts[e.target.value];
    setTokenToBalance(BigNumber(0));
    setTokenTo(newTokenTo);
    setAmountTo(BigNumber(0));

    setIsLoadingBalance(true);
    getTokenBalance(newTokenTo, setTokenToBalance);

    setIsLoadingSwapData(true);
    getSwapData();
  };

  const getFromApprovalAmount = useDebouncedCallback(async (token: ITokenContract = tokenFrom) => {
    if (token.address.toUpperCase() === contractManager.contracts.AVAX.address.toUpperCase()) {
      setFromApprovalAmount(ethers.constants.MaxUint256);
      setIsLoadingApproval(false);
      return;
    }

    let tokenApprovedAmount = BigNumber(0);

    try {
      tokenApprovedAmount = await props.getTokenApprovedAmount(token, tokenTo.name === "TE" ? contractManager.contracts.TokenHolder.address : TE_CONSTANTS.ZeroXExchangeProxy);
    }
    catch (error) {
      console.error(error);
    }

    setFromApprovalAmount(tokenApprovedAmount);
    setIsLoadingApproval(false);
  }, 500);

  const getTokenBalance = useDebouncedCallback(_getTokenBalance, 500);

  async function _getTokenBalance(token: ITokenContract, setter: Function) {
    let tokenBalance = BigNumber(0);

    try {
      tokenBalance = await props.getTokenBalance(token);
    }
    catch (error) {
      console.error(error);
    }

    setter(tokenBalance);
    setIsLoadingBalance(false);
  };

  const onLoad = useDebouncedCallback((_tokenFrom: ITokenContract = tokenFrom, _tokenTo: ITokenContract = tokenTo) => {
    if (!_tokenFrom.functions) {
      console.log("No _tokenFrom functions onLoad");
      console.log(_tokenFrom);
      return;
    }

    if (!_tokenTo.functions) {
      console.log("No _tokenTo functions onLoad");
      console.log(_tokenTo);
      return;
    }

    setTokenFrom(_tokenFrom);
    setTokenTo(_tokenTo);

    setIsLoadingApproval(true);
    getFromApprovalAmount(_tokenFrom);

    setIsLoadingBalance(true);
    _getTokenBalance(_tokenFrom, setTokenFromBalance);

    setIsLoadingBalance(true);
    _getTokenBalance(_tokenTo, setTokenToBalance);
  }, 500);

  // Drops the parameter passed in by the subscribeToBlockchain function
  const onLoad_noParams = React.useCallback(() => onLoad(), [onLoad]);

  React.useEffect(() => {
    subscribeToBlockchain(onLoad_noParams);
    return () => unsubscribeToBlockchain(onLoad_noParams);
  }, [onLoad_noParams, tokenFrom, tokenTo, subscribeToBlockchain, unsubscribeToBlockchain]);

  React.useEffect(() => {
    onLoad(contractManager.contracts.USDC, contractManager.contracts.TE);
  }, [onLoad, contractManager.contracts]);

  useInterval(() => {
    if (amountFrom.gt(0) && !isLoadingSwapData && swapData.deadline && Date.now() > Number(swapData.deadline) && tokenTo.name !== "TE") {
      console.info("Quote data expired, requesting new quote...");
      getSwapData();
    }
  }, 1000);

  const getSwapData = useDebouncedCallback(async () => {
    console.log("getSwapData() amountFrom:", amountFrom, typeof amountFrom);
    if (amountFrom.lte(0)) {
      setSwapData({ ...swapData, deadline: String(Date.now() + 30000) });
      setAmountTo(BigNumber(0));
      setExchangeRate(0);
      setIsLoadingSwapData(false);
      return;
    }

    if (tokenFrom.address === tokenTo.address) {
      setSwapData({ ...swapData, deadline: String(Date.now() + 30000) });
      setAmountTo(amountFrom);
      setExchangeRate(1);
      setIsLoadingSwapData(false);
      return;
    }

    console.log("tokenFrom", tokenFrom, tokenFrom.address);

    if (tokenTo.name === "TE") {
      if (!contractManager.contracts.TokenHolder.functions) {
        throw new Error("No TokenHolder functions in getSwapData()!");
      }

      const amountTE: ethers.BigNumber = await contractManager.contracts.TokenHolder.functions.getPriceQuote(tokenFrom.address, amountFrom);

      setAmountTo(amountTE);
      setExchangeRate(amountFrom.eq(0) ? 0 : safeBigNumberDiv(amountTE, amountFrom));

      const newSwapData: ISwapData = {
        ...defaultSwapData,
        to: contractManager.contracts.TokenHolder.address,
        sellAmount: amountFrom.toString(),
        buyAmount: amountTE.toString(),
        estimatedGas: tokenFrom.name === "USDC" ? "200000" : "400000",
        estimatedPriceImpact: tokenFrom.name === "USDC" ? "0" : "0.1",
        sources: [{ name: "Trendespresso", proportion: "1" }]
      };

      setSwapData(newSwapData);
      setIsLoadingSwapData(false);

      /** 
       * Spent $1 and get 10 TE
       * 1_000_000 == amountFrom
       * 1e6*1e6/100000 == 10,000,000 == 10 TE
       */
      return;
    }
    else if (tokenFrom.name === "TE") {
      // Selling is currently disabled
      return;
    }

    const URL =
      `https://avalanche.api.0x.org/swap/v1/quote?buyToken=${tokenTo.address}` +
      `&sellToken=${tokenFrom.address}` +
      `&sellAmount=${amountFrom.toString()}` +
      `&slippagePercentage=${currentSlippage}`;

    const result = await fetch(URL, { headers: TE_CONSTANTS.ZeroXAPIKeyHeader });
    const json = await result.json();

    if (result.status >= 400) {
      console.error(json);
      setSwapData(defaultSwapData);
      setAmountTo(BigNumber(0));
      setExchangeRate(0);
      setIsLoadingSwapData(false);
      return;
    }

    console.log(json);

    const newSwapData = json as ISwapData;
    newSwapData.deadline = (Number(newSwapData.deadline) - 1000).toString();

    setSwapData(newSwapData);
    setAmountTo(BigNumber(newSwapData.buyAmount));
    setExchangeRate(Number(newSwapData.price));
    setIsLoadingSwapData(false);
  }, 500);

  function flipFromTo() {
    const oldTokenFromBalance = tokenFromBalance;
    const oldTokenToBalance = tokenToBalance;
    const oldTokenFrom = tokenFrom;
    const oldTokenTo = tokenTo;
    const oldAmountFrom = amountFrom;
    const oldAmountTo = amountTo;

    setTokenFromBalance(oldTokenToBalance);
    setTokenToBalance(oldTokenFromBalance);
    setTokenFrom(oldTokenTo);
    setTokenTo(oldTokenFrom);
    setAmountFrom(oldAmountTo);
    setAmountTo(oldAmountFrom);
    setExchangeRate(exchangeRate ? 1 / exchangeRate : 0);

    setIsLoadingApproval(true);
    getFromApprovalAmount(oldTokenTo);

    setIsLoadingSwapData(true);
    getSwapData();
  };

  function actionButtonClicked(action: SwapActions) {
    switch (action) {
      case "APPROVE":
        approve(tokenFrom, tokenTo.name === "TE" ? contractManager.contracts.TokenHolder.address : TE_CONSTANTS.ZeroXExchangeProxy, amountFrom);
        break;
      case "SWAP":
        if (tokenTo.name === "TE") {
          props.swapTokenHolder(swapData, tokenFrom);
        }
        else {
          props.swap(swapData, tokenFrom);
        }

        setAmountFrom(BigNumber(0));
        setAmountFrom_str("");
        setAmountTo(BigNumber(0));
        setExchangeRate(0);
        break;
    }
  };

  let actionButton =
    tokenFrom.name === tokenTo.name ? (
      <button className={"etx-button Button fullwidth negative disabled"}>
        <div className="etx-button__icon Button__icon">
          <i className="fal fa-check-cross"></i>
        </div>
        <span className="etx-button__text Button__text">
          Invalid selection
        </span>
      </button>
    ) : tokenFrom.address.toUpperCase() !== contractManager.contracts.AVAX.address.toUpperCase() && (fromApprovalAmount.lte(0) || fromApprovalAmount.lt(amountFrom)) && (tokenFrom.name !== "USDC" || tokenTo.name !== "TE") ? (
      <button className={"etx-button Button fullwidth negative"} onClick={() => actionButtonClicked("APPROVE")}>
        <div className="etx-button__icon Button__icon">
          <i className="fal fa-check-circle"></i>
        </div>
        <span className="etx-button__text Button__text">Approve</span>
      </button>
    ) : amountFrom.lte(0) ? (
      <button className={"etx-button Button fullwidth negative disabled"}>
        <div className="etx-button__icon Button__icon">
          <i className="fal fa-circle-question"></i>
        </div>
        <span className="etx-button__text Button__text">Please enter an amount</span>
      </button>
    ) : isLoadingBalance || isLoadingApproval || isLoadingSwapData ? (
      <button className={"etx-button Button fullwidth negative disabled"}>
        <div className="etx-button__icon Button__icon">
          <i className="fal fa-circle-question"></i>
        </div>
        <span className="etx-button__text Button__text">Loading...</span>
      </button>
    ) : (
      <button className={"etx-button Button fullwidth positive"} onClick={() => actionButtonClicked("SWAP")}>
        <div className="etx-button__icon Button__icon">
          <i className="fal fa-arrow-down-to-arc"></i>
        </div>
        <span className="etx-button__text Button__text">Swap</span>
      </button>
    );

  return (
    <div className="etx-container Container pt-50 pb-0">
      <div className="etx-center">
        <div className="etx-card Card bg-dark-1-60 border-radius overflow-hidden">
          <div className="etx-card__item Card__item tab-s:ph-200 tab-s:pt-200 pb-50">
            <div className="etx-flex Flex c-auto av-center gh-50 gv-25">
              <div className="etx-flex__item Flex__item c-3 shrink-0">
                <div className="etx-text Text">
                  <p className="font-weight-medium">From</p>
                </div>
              </div>

              <div className="etx-flex__item Flex__item grow-1">
                <div className="FormField">
                  <div className="FormField__label">
                    <div className="FormField__label__item right">
                      <span>Available:</span>
                      <span>{toReadableNumber(tokenFromBalance, tokenFrom.decimals)}</span>
                    </div>
                  </div>

                  <div className="FormField__input">
                    <input
                      // type="number"
                      placeholder="0"
                      value={amountFrom_str}
                      onChange={onFromAmountSet}
                      onFocus={e => e.target.select()}
                    />
                    <button
                      className="FormField__input__button"
                      onClick={setMaxCash}
                    >
                      Max
                    </button>
                  </div>

                  <div className="FormField__label"></div>
                </div>
              </div>

              <div className="etx-flex__item Flex__item shrink-0">
                <div className="FormField">
                  <div className="FormField__input select">
                    <select
                      value={tokenFrom.name.replace(".", "")}
                      onChange={onFromCurrencySet}
                    >
                      {
                        tokenNames.map(tokenName =>
                          tokenName === "TE"
                            ? ""
                            : <option key={tokenName} value={tokenName.replace(".", "")}>
                              {tokenName}
                            </option>
                        )
                      }
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="etx-card__item Card__item ph-0 pv-0">
            <div className="CurrencySwitch">
              <hr className="dotted"></hr>

              <div className="CurrencySwitch__value">
                <span>{amountFrom.gt(0) ? "1.0000" : 0} {tokenFrom.name}</span>
              </div>

              <hr className="dotted"></hr>

              <a className="CurrencySwitch__button" onClick={flipFromTo}>
                <i className="fal fa-arrows-repeat"></i>
              </a>

              <hr className="dotted"></hr>

              <div className="CurrencySwitch__value">
                <span title={exchangeRate.toFixed(4) + " " + tokenTo.name}>≈ {exchangeRate.toFixed(4)} {tokenTo.name}</span>
              </div>

              <hr className="dotted"></hr>
            </div>
          </div>

          <div className="etx-card__item Card__item tab-s:ph-200 pt-50">
            <div className="etx-flex Flex c-auto av-center gh-50 gv-25">
              <div className="etx-flex__item Flex__item c-3 shrink-0">
                <div className="etx-text Text">
                  <p className="font-weight-medium">To</p>
                </div>
              </div>

              <div className="etx-flex__item Flex__item grow-1">
                <div className="FormField">
                  <div className="FormField__label">
                    <div className="FormField__label__item left">
                      <span>Estimated</span>
                    </div>
                  </div>

                  <div className="FormField__input">
                    <input
                      type="number"
                      placeholder="0"
                      value={toReadableNumber(amountTo, tokenTo.decimals)}
                      readOnly={true}
                    />
                  </div>

                  <div className="FormField__label">
                    <div className="FormField__label__item right">
                      <span>Balance:</span>
                      <span>{toReadableNumber(tokenToBalance, tokenTo.decimals)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="etx-flex__item Flex__item shrink-0">
                <div className="FormField">
                  <div className="FormField__input select">
                    <select
                      value={tokenTo.name.replace(/\./g, "")}
                      onChange={onToCurrencySet}
                    >
                      {
                        tokenNames.map(tokenName =>
                          <option key={tokenName} value={tokenName.replace(".", "")}>
                            {tokenName}
                          </option>
                        )
                      }
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="etx-card__item Card__item ph-0 pt-0 pb-50">
            <hr className="dotted"></hr>
          </div>

          {infoExpanded === true ? <>
            <div className="etx-card__item Card__item tab-s:ph-200 pv-75">
              <div className="etx-flex Flex c-auto av-center gh-50 gv-25">

                <div className="etx-flex__item Flex__item shrink-0">
                  <div className="etx-text Text">
                    <p className="font-label">Sources</p>
                  </div>
                </div>

                <div className="etx-flex__item Flex__item grow-1">
                  <div className="etx-flex Flex c-auto gv-50 gh-50 a-right">
                    {
                      swapData.sources.filter(source => Number(source.proportion) > 0).map(source =>
                        <div key={source.name} className="etx-flex__item Flex__item">
                          <div className="etx-text Text">
                            <p className="font-size-large">
                              <span>{source.name}: </span>
                              <span className="font-weight-medium">{(Number(source.proportion) * 100).toFixed(0)}%</span>
                            </p>
                          </div>
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
            </div>

            <div className="etx-card__item Card__item tab-s:ph-200 pv-0">
              <hr className="dotted"></hr>
            </div>

            <div className="etx-card__item Card__item tab-s:ph-200 pv-75">
              <div className="etx-flex Flex c-auto av-center gh-50 gv-25">
                <div className="etx-flex__item Flex__item shrink-0">
                  <div className="etx-text Text">
                    <p className="font-label">Estimated gas price</p>
                  </div>
                </div>

                <div className="etx-flex__item Flex__item grow-1">
                  <div className="etx-text Text a-right">
                    <p className="font-size-large font-weight-medium">{swapData.estimatedGas}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="etx-card__item Card__item tab-s:ph-200 pv-0">
              <hr className="dotted"></hr>
            </div>
            <div className="etx-card__item Card__item tab-s:ph-200 pv-75">
              <div className="etx-flex Flex c-auto av-center gh-50 gv-25">
                <div className="etx-flex__item Flex__item shrink-0">
                  <div className="etx-text Text">
                    <p className="font-label">Estimated price impact</p>
                  </div>
                </div>

                <div className="etx-flex__item Flex__item grow-1">
                  <div className="etx-text Text a-right">
                    <p className="font-size-large font-weight-medium">{swapData.estimatedPriceImpact + "%"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="etx-card__item Card__item tab-s:ph-200 pv-0">
              <hr className="dotted"></hr>
            </div>

            {
              Number(swapData?.expectedSlippage) > 0 ? <>
                <div className="etx-card__item Card__item tab-s:ph-200 pv-75">
                  <div className="etx-flex Flex c-auto av-center gh-50 gv-25">
                    <div className="etx-flex__item Flex__item shrink-0">
                      <div className="etx-text Text">
                        <p className="font-label">Expected slippage</p>
                      </div>
                    </div>

                    <div className="etx-flex__item Flex__item grow-1">
                      <div className="etx-text Text a-right">
                        <p className="font-size-large font-weight-medium">{swapData.expectedSlippage}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="etx-card__item Card__item tab-s:ph-200 pv-0">
                  <hr className="dotted"></hr>
                </div>
              </> : ""
            }

            <div className="etx-card__item Card__item tab-s:ph-200 pv-75">
              <div className="etx-flex Flex c-auto av-center gh-50 gv-25">
                <div className="etx-flex__item Flex__item shrink-0">
                  <div className="etx-text Text">
                    <p className="font-label">Limit addition price slippage</p>
                  </div>
                </div>

                <div className="etx-flex__item Flex__item grow-1">
                  <div className="etx-button-list ButtonList a-right gh-50 gv-50">
                    <a className={"etx-button Button compact" + (currentSlippage === 0.1 ? " color-swap-slippage" : " transparent")}>
                      <div className="etx-button__text Button__text" onClick={() => setCurrentSlippage(0.1)}>
                        <span className="font-weight-medium">0.1%</span>
                      </div>
                    </a>

                    <a className={"etx-button Button compact" + (currentSlippage === 0.5 ? " color-swap-slippage" : " transparent")}>
                      <div className="etx-button__text Button__text" onClick={() => setCurrentSlippage(0.5)}>
                        <span className="font-weight-medium">0.5%</span>
                      </div>
                    </a>

                    <a className={"etx-button Button compact" + (currentSlippage === 1.0 ? " color-swap-slippage" : " transparent")}>
                      <div className="etx-button__text Button__text" onClick={() => setCurrentSlippage(1)}>
                        <span className="font-weight-medium">1.0%</span>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="etx-card__item Card__item tab-s:ph-200 pv-0">
              <hr className="dotted"></hr>
            </div>
          </> : ""}

          <div className="etx-card__item Card__item tab-s:ph-200 pv-50">
            <div className="etx-button-list ButtonList a-center">
              <button className="etx-button Button transparent" onClick={() => setInfoExpanded(!infoExpanded)}>
                <span className="etx-button__text Button__text">{infoExpanded ? "Hide Details" : "Show Details"}</span>
                <div className="etx-button__icon Button__icon">
                  <i className={"fal " + (infoExpanded ? "fa-angle-up" : "fa-angle-down")}></i>
                </div>
              </button>
            </div>
          </div>

          <div className="etx-card__item Card__item ph-0 pt-50 pb-0">
            <hr className="dotted"></hr>
          </div>

          <div className="etx-card__item Card__item tab-s:ph-200 tab-s:pv-200">
            <div className="etx-button-list ButtonList">{actionButton}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
